import React from 'react';
import type { CrudConfig } from '@/components/shared/crud-page';
import { joinTitle, refField } from '@/lib/detail-title';
import { EMPLOYEE_REF_QUERY, FLEET_CONFIGS } from '@/pages/fleet-ops/configs';
import { ASSET_REF_QUERY } from '@/lib/ref-defaults';
import { fmtDate, fmtDateTime } from '@/lib/format-date';
import { AuditChangeCell } from './audit-change-cell';
import { AuditDetail } from './audit-detail';
import { WeightraxReviewCell, WEIGHTRAX_REVIEW_PATHS } from '@/components/shared/weightrax-review-cell';
import { ProductImportDialog, exportForImport } from '@/components/shared/record-import-dialog';
import { toast } from '@/components/ui/toast';

// People — intentionally empty after Drivers / CustomerProfiles / DriverAssignments were removed.
export const PEOPLE_CONFIGS: Record<string, CrudConfig> = {};

// Fleet extras (files, comments, asset lifecycle)
export const FLEET_EXTRAS_CONFIGS: Record<string, CrudConfig> = {
  // Offboard Asset is the symmetric entry point to Onboard Asset — same
  // sidebar treatment, but the config is defined alongside the active
  // Assets page in fleet-ops/configs.ts and just re-exported here so the
  // /fleet-extras/:entity router can find it under the key 'offboard-asset'.
  'offboard-asset': FLEET_CONFIGS['offboard-asset'],
  'vehicle-files': {
    title: 'Vehicle Files', singular: 'File', endpoint: '/vehicle-files',
    // Full Zoho `All_Vehicle_Files.json` parity.
    columns: [
      { key: 'fileName', label: 'File Name', filterType: 'text' },
      { key: 'fileUpload', label: 'File Upload', filterType: 'text' },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || '—' },
      { key: 'processed', label: 'Processed', filterType: 'boolean',
        render: (r) => r.processed ? 'Yes' : 'No' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
      { key: 'updatedAt', label: 'Modified', filterType: 'date',
        render: (r) => fmtDateTime(r.updatedAt) },
    ],
    fields: [
      { name: 'fileName', label: 'File Name', type: 'text' },
      { name: 'fileKey', label: 'File', type: 'file', uploadPrefix: 'vehicle-files' },
      { name: 'fileUpload', label: 'Legacy File URL (read-only)', type: 'text' },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
    ],
  },
  'vehicle-comments': {
    title: 'Vehicle Comments', singular: 'Comment', endpoint: '/vehicle-comments',
    columns: [
      { key: 'comment', label: 'Comment', filterType: 'text' },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || '—' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
    ],
    fields: [
      { name: 'comment', label: 'Comment', type: 'textarea' },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
    ],
  },
  'asset-transfers': {
    title: 'Asset Transfers', singular: 'Transfer', endpoint: '/asset-transfers', formSlug: 'asset_transfers', pageSlug: 'asset_transfer_page',
    // Aligned with Zoho `Asset_Transfers.json`. Workflow detail (per-task answers,
    // approver chain) lives in the related-list subform — Tier-B follow-up.
    columns: [
      { key: 'requestNumber', label: 'Request #', filterType: 'text' },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || '—' },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'Draft', label: 'Draft' },
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Complete', label: 'Complete' },
          { value: 'completed', label: 'Completed' },
        ] },
      { key: 'assetTransferred', label: 'Applied', filterType: 'boolean',
        render: (r) => r.assetTransferred ? 'Yes' : 'No' },
      { key: 'createdAt', label: 'Created', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
      { key: 'updatedAt', label: 'Updated', filterType: 'date',
        render: (r) => fmtDateTime(r.updatedAt) },
    ],
    rowActions: [
      { label: 'Open wizard', link: (row: any) => `/fleet-extras/asset-transfers/${row.id}/wizard` },
      { label: 'Finalize transfer',
        endpoint: '/asset-transfers/:id/finalize',
        actionKey: 'asset-transfer.finalize',
        confirm: 'Apply this transfer to the asset record? Department/Company/etc will be updated.',
        visible: (r) => !r.assetTransferred },
    ],
    canCreate: false,
    toolbarActions: [
      {
        label: '+ Start transfer',
        variant: 'default',
        onClick: async ({ navigate, refetch }) => {
          const { api } = await import('@/lib/api');
          const r = await api<any>('/asset-transfers', {
            method: 'POST',
            body: JSON.stringify({ status: 'Draft' }),
          });
          if (!r.success || !r.data?.id) {
            throw new Error(r.error?.message || 'Failed to create transfer');
          }
          refetch();
          navigate(`/fleet-extras/asset-transfers/${r.data.id}/wizard`);
        },
      },
    ],
    editAction: 'asset-transfer.edit-record',
    fields: [
      { name: 'requestNumber', label: 'Request Number', type: 'text' },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'Draft', label: 'Draft' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Complete', label: 'Complete' },
        { value: 'completed', label: 'Completed' },
      ] },
      { name: 'assetTransferred', label: 'Applied to Asset', type: 'boolean' },
    ],
    relatedLists: [
      { title: 'Transfer Tasks', endpoint: '/asset-transfer-task-answers', filterField: 'assetTransfer',
        inlineEditor: 'transfer-tasks',
        columns: [] },
    ],
  },
  'onboard-assets': {
    title: 'Asset Onboarding', singular: 'Onboarding', endpoint: '/onboard-assets', formSlug: 'onboard_assets', pageSlug: 'asset_onboarding_page',
    // Zoho `Onboard_Assets.json` ships 145 columns; the bulk are workflow flags
    // computed per task. The app surfaces the directly-relevant fields and lets
    // the Onboarding Tasks related list expose the remainder. (Tier-C: skipping
    // full parity by design.)
    columns: [
      { key: 'requestNumber', label: 'Request #', filterType: 'text' },
      { key: 'fleet', label: 'Fleet', filterType: 'text' },
      { key: 'registration', label: 'Rego', filterType: 'text' },
      { key: 'make', label: 'Make', filterType: 'text' },
      { key: 'model', label: 'Model', filterType: 'text' },
      { key: 'yearOfManufacture', label: 'Year', filterType: 'number' },
      { key: 'colour', label: 'Colour', filterType: 'text' },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'Draft', label: 'Draft' },
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Complete', label: 'Complete' },
        ] },
      { key: 'comments', label: 'Comments', filterType: 'text' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
      { key: 'updatedAt', label: 'Updated', filterType: 'date',
        render: (r) => fmtDateTime(r.updatedAt) },
    ],
    fields: [
      // Left blank, the server allocates the next number in sequence
      // (onboard-asset.model.ts). Typing one in overrides that.
      { name: 'requestNumber', label: 'Request Number', type: 'text', placeholder: 'Auto-assigned if left blank' },
      { name: 'fleet', label: 'Fleet', type: 'text' },
      { name: 'registration', label: 'Registration', type: 'text' },
      { name: 'make', label: 'Make', type: 'text' },
      { name: 'model', label: 'Model', type: 'text' },
      { name: 'yearOfManufacture', label: 'Year', type: 'number' },
      { name: 'colour', label: 'Colour', type: 'text' },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'Draft', label: 'Draft' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Complete', label: 'Complete' },
      ] },
      // iotProviderName field removed — the wizard's "GPS Type" task
      // (linked to IotSupplier) is the canonical IoT provider field.
      // Edit it via the wizard, not via this report's edit form.
      { name: 'comments', label: 'Comments', type: 'textarea' },
    ],
    relatedLists: [
      { title: 'Onboarding Tasks', endpoint: '/onboard-asset-task-answers', filterField: 'onboardAsset',
        inlineEditor: 'onboarding-tasks',
        columns: [] },
    ],
    rowActions: [
      // Primary entry point — opens the multi-stage wizard. For non-admin
      // roles without the action this is the *only* way to edit (see editAction below).
      { label: 'Open wizard', link: (row: any) => `/fleet-extras/onboard-assets/${row.id}/wizard` },
    ],
    // Replace the default "+ New" dialog with a toolbar action that creates
    // a stub OnboardAsset and immediately routes the user into the wizard.
    // Without this, users see two different create flows.
    canCreate: false,
    toolbarActions: [
      {
        label: '+ Start onboarding',
        variant: 'default',
        onClick: async ({ navigate, refetch }) => {
          // Lazy-import to keep this config file dep-free; the api helper
          // pulls auth + base URL from env and the global axios fetcher.
          const { api } = await import('@/lib/api');
          const r = await api<any>('/onboard-assets', {
            method: 'POST',
            body: JSON.stringify({ status: 'Draft' }),
          });
          if (!r.success || !r.data?.id) {
            throw new Error(r.error?.message || 'Failed to create onboarding');
          }
          refetch();
          navigate(`/fleet-extras/onboard-assets/${r.data.id}/wizard`);
        },
      },
    ],
    // Non-admins must use the wizard. Admins keep the row pencil + the
    // toolbar "+ Start onboarding" entry; non-admins see only "Start onboarding"
    // (since `editAction` here just hides the per-row pencil + "+ New").
    editAction: 'onboard-asset.edit-record',
  },
  // Asset Audit Trail — read-only, sourced from Atlas's own auditlogs
  // (entityType=Asset). Mirrors Zoho's Asset Audit Trail: Asset / Event / Who /
  // When + a colored field-change diff (AuditChangeCell). The legacy
  // RawAssetAudit table (2 rows) is no longer surfaced.
  'asset-audit-trail': {
    title: 'Asset Audit Trail', singular: 'Audit Entry', endpoint: '/asset-audit-trail',
    pageSlug: 'asset_audit_trail_page',
    canCreate: false, canEdit: false, canDelete: false, // read-only log
    columns: [
      { key: 'assetFleet', label: 'Asset', filterType: 'text', filterField: 'entityId',
        render: (r) => r.assetFleet || '—' },
      { key: 'eventType', label: 'Event', filterType: 'enum', filterField: 'operation',
        enumOptions: [
          { value: 'create', label: 'Create' },
          { value: 'update', label: 'Update / Edit' },
          { value: 'delete', label: 'Delete' },
        ],
        render: (r) => r.eventType || r.operation },
      { key: 'who', label: 'Who', render: (r) => r.who || '—' },
      { key: 'createdAt', label: 'When', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
      { key: 'changes', label: 'Details',
        render: (r) => React.createElement(AuditChangeCell, { row: r }) },
    ],
    fields: [], // read-only report — no create/edit form
    // The detail panel can't use the per-field grid (no schema fields); render
    // the audit summary + colored change diff instead, and give the panel a
    // human subtitle so it doesn't fall back to the raw audit-log id.
    detailTitle: (r) => {
      const asset = r.assetFleet ? `Asset ${r.assetFleet}` : null;
      return [asset, r.eventType].filter(Boolean).join(' · ') || 'Audit Entry';
    },
    detailContent: (r) => React.createElement(AuditDetail, { row: r }),
  },
  'asset-request-logs': {
    title: 'Asset Requests', singular: 'Request', endpoint: '/asset-request-logs',
    columns: [
      { key: 'requestType', label: 'Type', filterType: 'text' },
      { key: 'requestStatus', label: 'Status', filterType: 'text' },
      { key: 'fleet', label: 'Fleet', filterType: 'text' },
      { key: 'rego', label: 'Rego', filterType: 'text' },
      { key: 'make', label: 'Make', filterType: 'text' },
      { key: 'model', label: 'Model', filterType: 'text' },
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
      { key: 'updatedAt', label: 'Updated', filterType: 'date',
        render: (r) => fmtDateTime(r.updatedAt) },
    ],
    fields: [
      { name: 'requestType', label: 'Request Type', type: 'text' },
      { name: 'requestStatus', label: 'Status', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'fleet', label: 'Fleet', type: 'text' },
      { name: 'rego', label: 'Rego', type: 'text' },
      { name: 'make', label: 'Make', type: 'text' },
      { name: 'model', label: 'Model', type: 'text' },
    ],
  },
};

// Dispatch extras
export const DISPATCH_EXTRAS_CONFIGS: Record<string, CrudConfig> = {
  'run-sheets': {
    title: 'Run Sheets', singular: 'Run Sheet', endpoint: '/run-sheets', formSlug: 'run_sheets', pageSlug: 'run_sheets_page',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'date', label: 'Date', filterType: 'date',
        render: (r) => fmtDate(r.date) },
      { key: 'status', label: 'Status', filterType: 'text' },
      { key: 'branch.name', label: 'Branch',
        filterType: 'text', filterField: 'branch',
        render: (r) => r.branch?.name || '—' },
      { key: 'notes', label: 'Notes', filterType: 'text' },
      { key: 'assets', label: 'Assets', render: (r) => (r.assets || []).length },
      { key: '_view', label: 'Print', render: (r) => React.createElement('a', {
        href: `/dispatch-extras/run-sheets/${r.id}/print`,
        className: 'text-primary hover:underline text-xs font-medium',
        onClick: (e: any) => e.stopPropagation(),
      }, 'Open') },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text' },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'status', label: 'Status', type: 'text' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      { name: 'branch', label: 'Branch', type: 'ref', refEndpoint: '/master-data/branches', refLabel: 'name' },
    ],
  },
};

// Servicing extras
export const SERVICING_EXTRAS_CONFIGS: Record<string, CrudConfig> = {
  'upcoming-services': {
    title: 'Upcoming Services', singular: 'Upcoming Service', endpoint: '/upcoming-services',
    // Aligned with Zoho `All_Upcoming_Services_API.json`.
    columns: [
      { key: 'asset.fleetNumber', label: 'Vehicle',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || r.vehicle?.fleetNumber || '—' },
      { key: 'servicePlan.name', label: 'Service Plan',
        filterType: 'text', filterField: 'servicePlan',
        render: (r) => r.servicePlan?.name || '—' },
      { key: 'servicePlanSchedule.name', label: 'Schedule',
        filterType: 'text', filterField: 'servicePlanSchedule',
        render: (r) => r.servicePlanSchedule?.name || '—' },
      { key: 'nextServiceDate', label: 'Next Date', filterType: 'date',
        render: (r) => fmtDate(r.nextServiceDate) },
      { key: 'nextServiceValue', label: 'Next Value', filterType: 'number' },
      { key: 'unitOfMeasurement', label: 'Unit', filterType: 'text' },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'ok', label: 'OK' },
          { value: 'upcoming', label: 'Upcoming' },
          { value: 'due', label: 'Due' },
          { value: 'overdue', label: 'Overdue' },
          { value: 'unknown', label: 'Unknown' },
        ] },
      { key: 'processed', label: 'Processed', filterType: 'boolean',
        render: (r) => r.processed ? 'Yes' : 'No' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
    ],
    fields: [
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'nextServiceDate', label: 'Next Service Date', type: 'date' },
      { name: 'nextServiceValue', label: 'Next Service Value', type: 'number' },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'ok', label: 'OK' },
        { value: 'upcoming', label: 'Upcoming' },
        { value: 'due', label: 'Due' },
        { value: 'overdue', label: 'Overdue' },
        { value: 'unknown', label: 'Unknown' },
      ] },
    ],
  },
  'employee-service-records': {
    title: 'Employee Service Records', singular: 'Record', endpoint: '/employee-service-records',
    columns: [
      { key: 'employee.fullName', label: 'Employee',
        filterType: 'text', filterField: 'employee',
        render: (r) => r.employee?.fullName || '—' },
      { key: 'numberOfServices', label: '# Services', filterType: 'number' },
      { key: 'minuteSpent', label: 'Minutes', filterType: 'number' },
      { key: 'totalTimeSpent', label: 'Total Time', filterType: 'number' },
    ],
    fields: [
      { name: 'employee', label: 'Employee', type: 'ref', refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
      { name: 'numberOfServices', label: 'Number of Services', type: 'number' },
      { name: 'minuteSpent', label: 'Minutes Spent', type: 'number' },
      { name: 'totalTimeSpent', label: 'Total Time', type: 'number' },
    ],
  },
  'service-labour-costs': {
    title: 'Service Labour Costs', singular: 'Labour Cost', endpoint: '/service-labour-costs',
    columns: [
      { key: 'employee.fullName', label: 'Employee',
        filterType: 'text', filterField: 'employee',
        render: (r) => r.employee?.fullName || '—' },
      { key: 'hours', label: 'Hours', filterType: 'number' },
      { key: 'cost', label: 'Cost', filterType: 'number' },
      { key: 'serviceHistory.jobSheetNumber', label: 'Job Sheet',
        filterType: 'text', filterField: 'serviceHistory',
        render: (r) => r.serviceHistory?.jobSheetNumber || '—' },
    ],
    fields: [
      { name: 'serviceHistory', label: 'Service History', type: 'ref', refEndpoint: '/servicing/history', refLabel: 'jobSheetNumber' },
      { name: 'hours', label: 'Hours', type: 'number' },
      { name: 'cost', label: 'Cost', type: 'number' },
    ],
  },
  'service-material-costs': {
    title: 'Service Material Costs', singular: 'Material Cost', endpoint: '/service-material-costs',
    columns: [
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'cost', label: 'Cost', filterType: 'number' },
      { key: 'serviceHistory.jobSheetNumber', label: 'Job Sheet',
        filterType: 'text', filterField: 'serviceHistory',
        render: (r) => r.serviceHistory?.jobSheetNumber || '—' },
    ],
    fields: [
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'cost', label: 'Cost', type: 'number' },
    ],
  },
  'service-history-documents': {
    title: 'Service Documents', singular: 'Document', endpoint: '/service-history-documents',
    detailTitle: (r) => joinTitle(r.documentName, refField(r.serviceHistory, 'jobSheetNumber')),
    columns: [
      { key: 'documentName', label: 'Name', filterType: 'text' },
      { key: 'type', label: 'Type', filterType: 'text' },
      { key: 'fileUpload', label: 'File', filterType: 'text' },
      { key: 'serviceHistory.jobSheetNumber', label: 'Job Sheet',
        filterType: 'text', filterField: 'serviceHistory',
        render: (r) => r.serviceHistory?.jobSheetNumber || '—' },
    ],
    fields: [
      { name: 'documentName', label: 'Document Name', type: 'text' },
      { name: 'fileKey',  label: 'File',  type: 'file',  uploadPrefix: 'service-history-documents' },
      { name: 'imageKey', label: 'Image', type: 'image', uploadPrefix: 'service-history-documents' },
      { name: 'fileUpload', label: 'Legacy File URL (read-only)', type: 'text' },
      { name: 'image',      label: 'Legacy Image URL (read-only)', type: 'text' },
      { name: 'type', label: 'Type', type: 'text' },
      { name: 'serviceHistory', label: 'Service History', type: 'ref', refEndpoint: '/servicing/history', refLabel: 'jobSheetNumber' },
    ],
  },
  'service-plan-schedule-checklists': {
    title: 'Schedule Checklists', singular: 'Checklist', endpoint: '/service-plan-schedule-checklists',
    detailTitle: (r) => joinTitle(r.task) ?? joinTitle(refField(r.servicePlanSchedule, 'name')),
    columns: [
      // ref filters so deep-links from other reports (e.g. the "Edit
      // Checklist" link on the Service Plan's schedules list) pass an
      // ObjectId via `?servicePlan=<id>` / `?servicePlanSchedule=<id>`
      // and get an exact match — text-with-_contains was substring-
      // searching the display name, which silently returned zero rows.
      { key: 'servicePlan.name', label: 'Service Plan',
        filterType: 'ref', filterField: 'servicePlan',
        refEndpoint: '/servicing/plans', refLabel: 'name',
        render: (r) => r.servicePlan?.name || '—' },
      { key: 'servicePlanSchedule.name', label: 'Schedule',
        filterType: 'ref', filterField: 'servicePlanSchedule',
        refEndpoint: '/servicing/schedules', refLabel: 'name',
        render: (r) => r.servicePlanSchedule?.name || '—' },
    ],
    fields: [
      { name: 'servicePlan', label: 'Service Plan', type: 'ref', refEndpoint: '/servicing/plans', refLabel: 'name' },
      { name: 'servicePlanSchedule', label: 'Schedule', type: 'ref', refEndpoint: '/servicing/schedules', refLabel: 'name' },
    ],
    relatedLists: [
      { title: 'Checklist items', endpoint: '/service-plan-schedule-checklist-items', filterField: 'parent',
        columns: [
          { key: 'task', label: 'Task' },
          { key: 'groupHeader', label: 'Group' },
          { key: 'sortOrder', label: 'Sort' },
        ],
        // Inline edit + add support — clicking a row opens the edit dialog,
        // the "+ Add" button up top opens the same dialog empty. New items
        // get their `parent` ref auto-stamped from the surrounding checklist.
        rowEditFields: [
          { name: 'task', label: 'Task', type: 'text', required: true },
          { name: 'groupHeader', label: 'Group', type: 'text' },
          { name: 'sortOrder', label: 'Sort Order', type: 'number' },
        ],
        rowEditSingular: 'Checklist Item',
        rowCreate: true,
      },
    ],
  },
  checklists: {
    title: 'Checklists', singular: 'Checklist Item', endpoint: '/checklists', formSlug: 'checklists', pageSlug: 'checklists_page',
    detailTitle: (r) => joinTitle(r.listItem),
    columns: [
      { key: 'listItem', label: 'Item', filterType: 'text' },
      { key: 'checklistGroup.groupName', label: 'Group',
        filterType: 'text', filterField: 'checklistGroup',
        render: (r) => r.checklistGroup?.groupName || '—' },
    ],
    fields: [
      { name: 'listItem', label: 'List Item', type: 'text', required: true },
      { name: 'checklistGroup', label: 'Group', type: 'ref', refEndpoint: '/checklist-groups', refLabel: 'groupName' },
    ],
  },
  'checklist-groups': {
    title: 'Checklist Groups', singular: 'Group', endpoint: '/checklist-groups',
    detailTitle: (r) => joinTitle(r.groupName),
    columns: [
      { key: 'groupName', label: 'Group Name', filterType: 'text' },
      { key: 'groupHeadingDisplay', label: 'Heading', filterType: 'text' },
    ],
    fields: [
      { name: 'groupName', label: 'Group Name', type: 'text', required: true },
      { name: 'groupHeadingDisplay', label: 'Heading', type: 'text' },
    ],
    relatedLists: [
      { title: 'Checklist items', endpoint: '/checklists', filterField: 'checklistGroup',
        columns: [{ key: 'listItem', label: 'Item' }] },
    ],
  },
  'last-scheduled-services': {
    title: 'Last Scheduled Services', singular: 'Entry', endpoint: '/last-scheduled-services',
    // Full Zoho `Last_Scheduled_Services_Information_Report.json` parity.
    columns: [
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || r.vehicle?.fleetNumber || '—' },
      { key: 'serviceSchedule.name', label: 'Schedule',
        filterType: 'text', filterField: 'serviceSchedule',
        render: (r) => r.serviceSchedule?.name || '—' },
      { key: 'serviceHistory.jobSheetNumber', label: 'Service History',
        filterType: 'text', filterField: 'serviceHistory',
        render: (r) => r.serviceHistory?.jobSheetNumber || '—' },
      { key: 'date', label: 'Date', filterType: 'date',
        render: (r) => fmtDate(r.date) },
      { key: 'odo', label: 'Odo', filterType: 'number' },
      { key: 'hours', label: 'Hours', filterType: 'number' },
      { key: 'hubo', label: 'Hubo', filterType: 'number' },
      { key: 'processed', label: 'Processed', filterType: 'boolean',
        render: (r) => r.processed ? 'Yes' : 'No' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
    ],
    fields: [
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'odo', label: 'Odometer', type: 'number' },
    ],
  },
  'old-service-history': {
    title: 'Old Service History', singular: 'Old Record', endpoint: '/old-service-history',
    // Full Zoho `All_Old_Service_History_Reports.json` parity.
    columns: [
      { key: 'jobNo', label: 'Job No', filterType: 'text' },
      { key: 'jobDate', label: 'Job Date', filterType: 'date',
        render: (r) => fmtDate(r.jobDate) },
      { key: 'fleetNo', label: 'Fleet No', filterType: 'text' },
      { key: 'rego', label: 'Rego', filterType: 'text' },
      { key: 'jobCode', label: 'Job Code', filterType: 'text' },
      { key: 'jobDesc', label: 'Job Desc.', filterType: 'text' },
      { key: 'technician', label: 'Technician', filterType: 'text' },
      { key: 'repairer', label: 'Repairer', filterType: 'text' },
      { key: 'partsCost', label: 'Parts Cost', filterType: 'number' },
      { key: 'notes', label: 'Notes', filterType: 'text' },
      { key: 'done', label: 'Done', filterType: 'boolean',
        render: (r) => r.done ? 'Yes' : 'No' },
    ],
    fields: [
      { name: 'jobNo', label: 'Job No', type: 'text' },
      { name: 'jobDate', label: 'Job Date', type: 'date' },
      { name: 'fleetNo', label: 'Fleet No', type: 'text' },
      { name: 'rego', label: 'Rego', type: 'text' },
      { name: 'jobDesc', label: 'Job Description', type: 'textarea' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
      { name: 'technician', label: 'Technician', type: 'text' },
      { name: 'repairer', label: 'Repairer', type: 'text' },
      { name: 'partsCost', label: 'Parts Cost', type: 'number' },
    ],
  },
  'old-job-sheets': {
    title: 'Old Job Sheets', singular: 'Old Sheet', endpoint: '/old-job-sheets',
    // Full Zoho `All_Old_Job_Sheets.json` parity.
    columns: [
      { key: 'jobSheetNumber', label: 'Sheet #', filterType: 'text' },
      { key: 'fleetNo', label: 'Fleet No', filterType: 'text' },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || '—' },
      { key: 'date', label: 'Date', filterType: 'date',
        render: (r) => fmtDate(r.date) },
      { key: 'technician', label: 'Technician', filterType: 'text' },
      { key: 'speedo', label: 'Speedo', filterType: 'number' },
      { key: 'hubo', label: 'Hubo', filterType: 'number' },
      { key: 'hours', label: 'Hours', filterType: 'number' },
      { key: 'jobCode', label: 'Job Code', filterType: 'text' },
      { key: 'jobDescription', label: 'Job Description', filterType: 'text' },
      { key: 'descriptionOfWorkDone', label: 'Description of Work Done', filterType: 'text' },
      { key: 'faultDetailsAndInstructions', label: 'Fault Details', filterType: 'text' },
      { key: 'done', label: 'Done', filterType: 'boolean',
        render: (r) => r.done ? 'Yes' : 'No' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
    ],
    fields: [
      { name: 'jobSheetNumber', label: 'Job Sheet Number', type: 'text' },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'descriptionOfWorkDone', label: 'Description of Work', type: 'textarea' },
      { name: 'faultDetailsAndInstructions', label: 'Fault Details', type: 'textarea' },
      { name: 'jobCode', label: 'Job Code', type: 'text' },
      { name: 'jobDescription', label: 'Job Description', type: 'text' },
      { name: 'hours', label: 'Hours', type: 'number' },
      { name: 'hubo', label: 'Hubo', type: 'number' },
      { name: 'speedo', label: 'Speedo', type: 'number' },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
    ],
  },
};

// Operations
export const OPERATIONS_CONFIGS: Record<string, CrudConfig> = {
  orders: {
    title: 'Orders', singular: 'Order', endpoint: '/orders',
    // Full Zoho `All_Orders_List.json` parity.
    columns: [
      { key: 'orderNumber', label: 'Order #', filterType: 'text' },
      { key: 'poNumber', label: 'PO #', filterType: 'text' },
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'customer.company', label: 'Customer',
        filterType: 'text', filterField: 'customer',
        render: (r) => r.customer?.company || '—' },
      { key: 'branch.name', label: 'Branch',
        filterType: 'text', filterField: 'branch',
        render: (r) => r.branch?.name || '—' },
      { key: 'dateOrdered', label: 'Date Ordered', filterType: 'date',
        render: (r) => fmtDate(r.dateOrdered) },
      { key: 'datePickedUp', label: 'Date Picked Up', filterType: 'date',
        render: (r) => fmtDate(r.datePickedUp) },
      { key: 'deliveryOption', label: 'Delivery Option', filterType: 'text' },
      { key: 'total', label: 'Total', filterType: 'number' },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'Open', label: 'Open' },
          { value: 'Ordered', label: 'Ordered' },
          { value: 'Ready for Pick Up', label: 'Ready for Pick Up' },
          { value: 'Collected', label: 'Collected' },
          { value: 'Cancelled', label: 'Cancelled' },
        ] },
      { key: 'comment', label: 'Comment', filterType: 'text' },
    ],
    fields: [
      { name: 'orderNumber', label: 'Order Number', type: 'text' },
      { name: 'poNumber', label: 'PO Number', type: 'text' },
      { name: 'dateOrdered', label: 'Date Ordered', type: 'date' },
      { name: 'deliveryOption', label: 'Delivery Option', type: 'text' },
      { name: 'comment', label: 'Comment', type: 'textarea' },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'Open', label: 'Open' },
        { value: 'Ordered', label: 'Ordered' },
        { value: 'Ready for Pick Up', label: 'Ready for Pick Up' },
        { value: 'Collected', label: 'Collected' },
        { value: 'Cancelled', label: 'Cancelled' },
      ] },
      { name: 'total', label: 'Total', type: 'number' },
      { name: 'customer', label: 'Customer', type: 'ref', refEndpoint: '/master-data/customers', refLabel: 'company' },
      { name: 'branch', label: 'Branch', type: 'ref', refEndpoint: '/master-data/branches', refLabel: 'name' },
    ],
    relatedLists: [
      { title: 'Order items', endpoint: '/order-items', filterField: 'order',
        columns: [
          { key: 'description', label: 'Description' },
          { key: 'quantity', label: 'Qty' },
          { key: 'price', label: 'Price' },
          { key: 'total', label: 'Total' },
        ] },
    ],
  },
  'order-items': {
    title: 'Order Items', singular: 'Order Item', endpoint: '/order-items',
    columns: [
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'quantity', label: 'Qty', filterType: 'number' },
      { key: 'price', label: 'Price', filterType: 'number' },
      { key: 'total', label: 'Total', filterType: 'number' },
      { key: 'order.orderNumber', label: 'Order #',
        filterType: 'text', filterField: 'order',
        render: (r) => r.order?.orderNumber || '—' },
    ],
    fields: [
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'quantity', label: 'Quantity', type: 'number' },
      { name: 'price', label: 'Price', type: 'number' },
      { name: 'total', label: 'Total', type: 'number' },
      { name: 'order', label: 'Order', type: 'ref', refEndpoint: '/orders', refLabel: 'orderNumber' },
    ],
  },
  materials: {
    title: 'Materials', singular: 'Material', endpoint: '/materials', formSlug: 'materials', pageSlug: 'materials_page',
    columns: [
      { key: 'materialName', label: 'Name', filterType: 'text' },
      { key: 'cost', label: 'Cost', filterType: 'number' },
      { key: 'qtyOnHand', label: 'Qty On Hand', filterType: 'number' },
      { key: 'unit.name', label: 'Unit',
        filterType: 'text', filterField: 'unit',
        render: (r) => r.unit?.name || '—' },
    ],
    fields: [
      { name: 'materialName', label: 'Material Name', type: 'text', required: true },
      { name: 'cost', label: 'Cost', type: 'number' },
      { name: 'qtyOnHand', label: 'Qty On Hand', type: 'number' },
      { name: 'unit', label: 'Unit', type: 'ref', refEndpoint: '/master-data/units', refLabel: 'name' },
    ],
    relatedLists: [
      { title: 'Used in job materials', endpoint: '/job-materials', filterField: 'material',
        columns: [
          { key: 'qty', label: 'Qty' },
          { key: 'cost', label: 'Cost' },
          { key: 'total', label: 'Total' },
        ] },
    ],
  },
  'job-materials': {
    title: 'Job Materials', singular: 'Job Material', endpoint: '/job-materials',
    // Full Zoho `All_Job_Materials.json` parity.
    columns: [
      { key: 'material.materialName', label: 'Material',
        filterType: 'text', filterField: 'material',
        render: (r) => r.material?.materialName || '—' },
      { key: 'qty', label: 'Qty', filterType: 'number' },
      { key: 'cost', label: 'Cost', filterType: 'number' },
      { key: 'total', label: 'Total', filterType: 'number' },
      { key: 'unit', label: 'Unit', filterType: 'text' },
      { key: 'jobCard.jobSheetNumber', label: 'Job Card',
        filterType: 'text', filterField: 'jobCard',
        render: (r) => r.jobCard?.jobSheetNumber || '—' },
      { key: 'addedBy.fullName', label: 'Added By',
        filterType: 'text', filterField: 'addedBy',
        render: (r) => r.addedBy?.fullName || '—' },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
    ],
    fields: [
      { name: 'jobCard', label: 'Job Card', type: 'ref', refEndpoint: '/fleet/job-cards', refLabel: 'jobSheetNumber' },
      { name: 'material', label: 'Material', type: 'ref', refEndpoint: '/materials', refLabel: 'materialName' },
      { name: 'qty', label: 'Qty', type: 'number' },
      { name: 'cost', label: 'Cost', type: 'number' },
    ],
  },
  'time-logs': {
    title: 'Time Logs', singular: 'Time Log', endpoint: '/time-logs', formSlug: 'time_logs', pageSlug: 'time_logs_page', ownerField: 'employee',
    // Full Zoho `Time_Log_View.json` parity.
    columns: [
      { key: 'employee.fullName', label: 'Employee',
        filterType: 'text', filterField: 'employee',
        render: (r) => r.employee?.fullName || '—' },
      { key: 'jobCard.jobSheetNumber', label: 'Job Sheet #',
        filterType: 'text', filterField: 'jobCard',
        render: (r) => r.jobCard?.jobSheetNumber || '—' },
      { key: 'serviceHistory.jobSheetNumber', label: 'Service',
        filterType: 'text', filterField: 'serviceHistory',
        render: (r) => r.serviceHistory?.jobSheetNumber || '—' },
      { key: 'startTime', label: 'Start Time', filterType: 'date',
        render: (r) => fmtDateTime(r.startTime) },
      { key: 'endTime', label: 'End Time', filterType: 'date',
        render: (r) => fmtDateTime(r.endTime, 'open') },
      { key: 'logDate', label: 'Log Date', filterType: 'date',
        render: (r) => fmtDate(r.logDate) },
      { key: 'hours', label: 'Hours', filterType: 'number' },
      { key: 'remarks', label: 'Remarks', filterType: 'text' },
    ],
    fields: [
      { name: 'employee', label: 'Employee', type: 'ref', refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
      { name: 'jobCard', label: 'Job Card', type: 'ref', refEndpoint: '/fleet/job-cards', refLabel: 'jobSheetNumber' },
      { name: 'logDate', label: 'Log Date', type: 'date' },
      // Start/End drive the auto-computed `hours` column server-side via a
      // pre-save hook on the TimeLog model. Operators no longer enter
      // hours directly — they bracket the work window and the system
      // does the maths, matching how the technician's timer flow works.
      { name: 'startTime', label: 'Start Time', type: 'datetime' },
      { name: 'endTime', label: 'End Time', type: 'datetime' },
      { name: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    validate: (v) => {
      if (!v.startTime || !v.endTime) return null;
      const start = new Date(v.startTime).getTime();
      const end   = new Date(v.endTime).getTime();
      if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
      if (end <= start) return { endTime: 'End time must be after start time.' };
      return null;
    },
  },
  photos: {
    title: 'Photos', singular: 'Photo', endpoint: '/photos',
    detailTitle: (r) => joinTitle(r.type || 'Photo', refField(r.jobSheet, 'jobSheetNumber')),
    columns: [
      { key: 'type', label: 'Type', filterType: 'text' },
      { key: 'takeAPhoto', label: 'URL', render: (r) => r.takeAPhoto ? '✓ has URL' : '—' },
      { key: 'imageKey', label: 'S3 Key', filterType: 'text',
        render: (r) => r.imageKey ? '✓ in S3' : '—' },
      { key: 'jobSheet.jobSheetNumber', label: 'Job Sheet',
        filterType: 'text', filterField: 'jobSheet',
        render: (r) => r.jobSheet?.jobSheetNumber || '—' },
    ],
    fields: [
      { name: 'imageKey',  label: 'Photo', type: 'image', uploadPrefix: 'photos' },
      { name: 'takeAPhoto', label: 'Legacy Photo URL (read-only)', type: 'text' },
      { name: 'type', label: 'Type', type: 'text' },
      { name: 'jobSheet', label: 'Job Sheet', type: 'ref', refEndpoint: '/old-job-sheets', refLabel: 'jobSheetNumber' },
    ],
  },
  'catalogue-groups': {
    title: 'Catalogue Groups', singular: 'Group', endpoint: '/catalogue-groups',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'itemCatCode', label: 'Code', filterType: 'text' },
      { key: 'description', label: 'Description', filterType: 'text' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text', required: true },
      { name: 'itemCatCode', label: 'Item Cat Code', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'image', label: 'Image URL', type: 'text' },
    ],
    relatedLists: [
      { title: 'Items in group', endpoint: '/catalogue-items', filterField: 'group',
        columns: [
          { key: 'itemCode', label: 'Code' },
          { key: 'description', label: 'Description' },
          { key: 'price', label: 'Price' },
        ] },
      { title: 'Order lines with this group', endpoint: '/order-items', filterField: 'group',
        columns: [
          { key: 'description', label: 'Description' },
          { key: 'quantity', label: 'Qty' },
          { key: 'total', label: 'Total' },
        ] },
    ],
  },
  'catalogue-items': {
    title: 'Products', singular: 'Product', endpoint: '/catalogue-items', formSlug: 'catalogue_items', pageSlug: 'catalogue_items_page',
    columns: [
      // Server pre-signs imageKey → imageUrl on every read.
      { key: 'imageUrl', label: 'Image', exportFormat: 'image',
        render: (r) => r.imageUrl
          ? React.createElement('img', { src: r.imageUrl, alt: r.itemCode ?? '', className: 'h-10 w-10 object-cover rounded' })
          : '—' },
      { key: 'itemCode', label: 'Code', filterType: 'text' },
      { key: 'jdeMaterialCode', label: 'JDE Code', filterType: 'text',
        // Aliases share a primary's JDE code and aren't pushed themselves.
        render: (r) => (r.jdeMaterialCode ? `${r.jdeMaterialCode}${r.jdePrimary ? '' : ' (alias)'}` : '—') },
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'price', label: 'Price', filterType: 'number' },
      { key: 'group.name', label: 'Group',
        filterType: 'text', filterField: 'group',
        render: (r) => r.group?.name || '—' },
      { key: 'weightraxSync', label: 'Weightrax',
        render: (r) => {
          const sync: any[] = Array.isArray(r.weightraxSync) ? r.weightraxSync : [];
          if (r.excludeFromWeightrax) return 'Excluded';
          if (!r.jdeMaterialCode) return 'Needs JDE code';
          if (!r.jdePrimary) return 'Alias';
          if (sync.some((x) => x.error)) return 'Failed';
          if (sync.some((x) => x.linkedExisting)) return r.archived ? 'Linked — needs review' : React.createElement(WeightraxReviewCell, { kind: 'product', row: r });
          return sync.length && sync.every((x) => x.materialId) ? 'Synced' : 'Pending';
        } },
      { key: 'archived', label: 'Status', render: (r) => (r.archived ? 'Archived' : 'Active') },
    ],
    // The server hides archived products unless asked (so pickers never offer
    // them); the unfiltered first tab is therefore Active.
    tabs: [
      { label: 'Active' },
      // Its own card view — see pages/weightrax-review.
      { label: 'Needs review', to: WEIGHTRAX_REVIEW_PATHS.product },
      { label: 'Archived', filter: { archived: 'true' } },
      { label: 'All', filter: { includeArchived: '1' } },
    ],
    fields: [
      { name: 'itemCode', label: 'Item Code', type: 'text', required: true },
      // Non-weighbridge items (hardware, cartage, hire…) are excluded and need
      // neither a JDE code nor a Site.
      { name: 'excludeFromWeightrax', label: 'Exclude from Weightrax', type: 'boolean' },
      // From JDE (typed here or CSV-imported); sent to Weightrax as the material code.
      { name: 'jdeMaterialCode', label: 'JDE Material Code (required unless excluded from Weightrax)', type: 'text',
        placeholder: 'e.g. GAP40' },
      { name: 'sites', label: 'Sites (required unless excluded from Weightrax)', type: 'multi-ref',
        refEndpoint: '/master-data/sites', refLabel: 'name' },
      { name: 'description', label: 'Description', type: 'textarea' },
      // Matched by name to the Weightrax site's units; blank goes as tonnes.
      { name: 'unit', label: 'Unit (e.g. t, m3)', type: 'text' },
      { name: 'price', label: 'Price', type: 'number' },
      { name: 'imageKey', label: 'Image', type: 'image', uploadPrefix: 'catalogue' },
      { name: 'group', label: 'Group', type: 'ref', refEndpoint: '/catalogue-groups', refLabel: 'name' },
    ],
    validate: (v): Record<string, string> | null => {
      if (v.excludeFromWeightrax) return null;
      if (!String(v.jdeMaterialCode ?? '').trim()) return { jdeMaterialCode: 'Enter the JDE material code, or exclude the product from Weightrax' };
      if (!Array.isArray(v.sites) || v.sites.length === 0) return { sites: 'Pick at least one site, or exclude the product from Weightrax' };
      return null;
    },
    // Round trip for bulk edits (e.g. JDE codes): export → edit in Excel →
    // import with a preview. See components/shared/record-import-dialog.
    toolbarActions: [
      { label: 'Export for import', variant: 'outline',
        onClick: async () => { try { await exportForImport('product'); } catch (e: any) { toast('Export failed', e.message, 'error'); } } },
      { label: 'Import CSV', variant: 'outline', customDialog: ProductImportDialog },
    ],
    rowActions: [
      { label: 'Archive', endpoint: '/catalogue-items/:id/archive', method: 'POST',
        confirm: 'Archive this product? It will be hidden from pickers and marked obsolete in Weightrax (unless another product still uses its JDE code). Existing jobs keep it.',
        visible: (r) => !r.archived },
      { label: 'Restore', endpoint: '/catalogue-items/:id/unarchive', method: 'POST',
        visible: (r) => !!r.archived },
      { label: 'Make primary', endpoint: '/catalogue-items/:id/make-primary', method: 'POST',
        confirm: 'Make this the product pushed to Weightrax for its JDE code? The current primary becomes an alias.',
        visible: (r) => !!r.jdeMaterialCode && !r.jdePrimary && !r.archived && !r.excludeFromWeightrax },
      { label: 'Push to Weightrax', endpoint: '/catalogue-items/:id/weightrax-sync', method: 'POST',
        visible: (r) => !!r.jdePrimary && !r.archived && !r.excludeFromWeightrax
          && (!r.weightraxSync?.length || r.weightraxSync.some((x: any) => x.error || !x.materialId)) },
    ],
    relatedLists: [
      { title: 'Order lines', endpoint: '/order-items', filterField: 'catalogueItem',
        columns: [
          { key: 'description', label: 'Description' },
          { key: 'quantity', label: 'Qty' },
          { key: 'price', label: 'Price' },
        ] },
      { title: 'Recycled aggregates of this product', endpoint: '/recycled-aggregates', filterField: 'product',
        columns: [
          { key: 'quantity', label: 'Qty' },
          { key: 'date', label: 'Date' },
        ] },
    ],
  },
  issues: {
    title: 'Issues', singular: 'Issue', endpoint: '/issues',
    // Full Zoho `All_Issues.json` parity.
    columns: [
      { key: 'title', label: 'Title', filterType: 'text' },
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'asset.fleetNumber', label: 'Asset',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || '—' },
      { key: 'priority', label: 'Priority', filterType: 'enum',
        enumOptions: [
          { value: 'High', label: 'High' },
          { value: 'Medium', label: 'Medium' },
          { value: 'Low', label: 'Low' },
        ] },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'Open', label: 'Open' },
          { value: 'In Progress', label: 'In Progress' },
          { value: 'Resolved', label: 'Resolved' },
          { value: 'Closed', label: 'Closed' },
        ] },
      { key: 'assignee.fullName', label: 'Assignee',
        filterType: 'text', filterField: 'assignee',
        render: (r) => r.assignee?.fullName || '—' },
      { key: 'dueDate', label: 'Due Date', filterType: 'date',
        render: (r) => fmtDate(r.dueDate) },
      { key: 'fileUpload', label: 'File', filterType: 'text' },
      { key: 'resolvedAt', label: 'Resolved', filterType: 'date',
        render: (r) => fmtDate(r.resolvedAt) },
      { key: 'createdAt', label: 'Added', filterType: 'date',
        render: (r) => fmtDate(r.createdAt) },
    ],
    fields: [
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'priority', label: 'Priority', type: 'select', options: [
        { value: 'High', label: 'High' },
        { value: 'Medium', label: 'Medium' },
        { value: 'Low', label: 'Low' },
      ] },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'Open', label: 'Open' },
        { value: 'In Progress', label: 'In Progress' },
        { value: 'Resolved', label: 'Resolved' },
        { value: 'Closed', label: 'Closed' },
      ] },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
    ],
  },
  'recycled-aggregates': {
    title: 'Recycled Aggregates', singular: 'Entry', endpoint: '/recycled-aggregates',
    detailTitle: (r) => joinTitle(refField(r.product, 'itemCode'), r.date ? fmtDate(r.date) : null),
    // Full Zoho `All_Aggregates.json` parity.
    columns: [
      { key: 'product.itemCode', label: 'Product',
        filterType: 'text', filterField: 'product',
        render: (r) => r.product?.itemCode || '—' },
      { key: 'aggregateType', label: 'Aggregate Type', filterType: 'text' },
      { key: 'projectName', label: 'Project Name', filterType: 'text' },
      { key: 'address', label: 'Address', filterType: 'text' },
      { key: 'quantity', label: 'Qty', filterType: 'number' },
      { key: 'cartage', label: 'Cartage', filterType: 'number' },
      { key: 'deliveryRate', label: 'Delivery Rate', filterType: 'number' },
      { key: 'startDateTime', label: 'Start', filterType: 'date',
        render: (r) => fmtDateTime(r.startDateTime) },
      { key: 'endDateTime', label: 'End', filterType: 'date',
        render: (r) => fmtDateTime(r.endDateTime) },
      { key: 'date', label: 'Date', filterType: 'date',
        render: (r) => fmtDate(r.date) },
      { key: 'notes', label: 'Notes', filterType: 'text' },
    ],
    fields: [
      { name: 'product', label: 'Product', type: 'ref', refEndpoint: '/catalogue-items', refLabel: 'itemCode' },
      { name: 'quantity', label: 'Quantity', type: 'number' },
      { name: 'date', label: 'Date', type: 'date' },
      { name: 'notes', label: 'Notes', type: 'textarea' },
    ],
  },
};

// Integration / logs (mostly read-only)
export const INTEGRATION_CONFIGS: Record<string, CrudConfig> = {
  'sms-history': {
    title: 'SMS History', singular: 'SMS', endpoint: '/sms-history',
    detailTitle: (r) => joinTitle(r.phoneNumber),
    canCreate: false, canDelete: false,
    columns: [
      { key: 'createdAt', label: 'Sent', filterType: 'date',
        render: (r) => fmtDateTime(r.createdAt) },
      { key: 'phoneNumber', label: 'Phone', filterType: 'text' },
      { key: 'message', label: 'Message', filterType: 'text' },
      { key: 'deliveryStatus', label: 'Status', filterType: 'text' },
      { key: 'messageId', label: 'Message ID', filterType: 'text' },
    ],
    fields: [
      { name: 'phoneNumber', label: 'Phone Number', type: 'text' },
      { name: 'message', label: 'Message', type: 'textarea' },
      { name: 'deliveryStatus', label: 'Delivery Status', type: 'text' },
      { name: 'messageId', label: 'Provider Message ID', type: 'text' },
    ],
  },
  'email-history': {
    title: 'Email History', singular: 'Email', endpoint: '/email-history',
    detailTitle: (r) => joinTitle(r.subject) ?? joinTitle(r.to),
    canCreate: false, canDelete: false,
    // Only offered on failed IoT supplier rows. The server regenerates the
    // message from the linked asset + current template rather than replaying
    // the retained `body`, so a retry picks up recipient / wording fixes made
    // since, and refuses a removal retry when the asset is back in service.
    rowActions: [
      {
        label: 'Retry send',
        actionKey: 'email-history.retry',
        endpoint: '/email-history/:id/retry',
        method: 'POST',
        confirm: 'Regenerate and resend this supplier email? It uses the template and recipients configured now, which may differ from the original attempt.',
        visible: (r) => r.status === 'failed' && r.category === 'iot-supplier',
      },
    ],
    tabs: [
      { label: 'All' },
      { label: 'Portal invitations', filter: { category: 'portal-invitation' } },
      { label: 'Magic links',         filter: { category: 'magic-link' } },
      { label: 'Password resets',     filter: { category: 'password-reset' } },
      { label: 'IoT supplier',        filter: { category: 'iot-supplier' } },
      { label: 'Job card reviews',    filter: { category: 'job-card-review' } },
      { label: 'Failed sends',        filter: { status: 'failed' } },
    ],
    columns: [
      { key: 'sentAt', label: 'Sent', filterType: 'date',
        render: (r) => fmtDateTime(r.sentAt) },
      { key: 'to', label: 'To', filterType: 'text' },
      { key: 'subject', label: 'Subject', filterType: 'text' },
      { key: 'category', label: 'Category', filterType: 'enum',
        enumOptions: [
          { value: 'portal-invitation', label: 'Portal invitation' },
          { value: 'magic-link',        label: 'Magic link' },
          { value: 'password-reset',    label: 'Password reset' },
          { value: 'iot-supplier',      label: 'IoT supplier' },
          { value: 'job-card-review',   label: 'Job card review' },
          { value: 'other',             label: 'Other' },
        ] },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'sent',   label: 'Sent' },
          { value: 'failed', label: 'Failed' },
        ] },
      { key: 'provider', label: 'Provider', filterType: 'enum',
        enumOptions: [
          { value: 'smtp',     label: 'SMTP' },
          { value: 'sendgrid', label: 'SendGrid' },
        ] },
      { key: 'error', label: 'Error', filterType: 'text', render: (r) => r.error || '—' },
    ],
    fields: [
      { name: 'sentAt',        label: 'Sent At',      type: 'datetime' },
      { name: 'to',            label: 'Recipients',   type: 'text' },
      { name: 'from',          label: 'From',         type: 'text' },
      { name: 'subject',       label: 'Subject',      type: 'text' },
      { name: 'category',      label: 'Category',     type: 'text' },
      { name: 'provider',      label: 'Provider',     type: 'text' },
      { name: 'status',        label: 'Status',       type: 'text' },
      { name: 'error',         label: 'Error',        type: 'textarea' },
      { name: 'relatedEntity', label: 'Related Type', type: 'text' },
      { name: 'relatedId',     label: 'Related ID',   type: 'text' },
      { name: 'bodyPreview',   label: 'Body Preview', type: 'textarea' },
      // Full HTML as sent. Older rows predate retention and show empty —
      // `bodyPreview` remains the only record for those.
      { name: 'body',          label: 'Body (full)',  type: 'textarea' },
    ],
  },
  'api-call-logs': {
    title: 'API Call Logs', singular: 'Log', endpoint: '/api-call-logs',
    detailTitle: (r) => joinTitle(r.integrationName, r.status),
    columns: [
      { key: 'integrationName', label: 'Integration', filterType: 'text' },
      { key: 'status', label: 'Status', filterType: 'enum',
        enumOptions: [
          { value: 'success', label: 'Success' },
          { value: 'error', label: 'Error' },
          { value: 'pending', label: 'Pending' },
        ] },
      { key: 'durationMs', label: 'Duration (ms)', filterType: 'number' },
      { key: 'errorMessage', label: 'Error', filterType: 'text' },
      { key: 'executedAt', label: 'Executed At', filterType: 'date',
        render: (r) => fmtDateTime(r.executedAt) },
    ],
    // Detail dialog. requestPayload / responsePayload are textareas so the
    // operator can scroll long Carjam / IoT-sync bodies. Legacy rows from
    // before payload logging existed will show empty boxes — that's by
    // design (the data was never captured); current calls populate both.
    fields: [
      { name: 'integrationName', label: 'Integration Name', type: 'text' },
      { name: 'status', label: 'Status', type: 'select', options: [
        { value: 'success', label: 'Success' },
        { value: 'error', label: 'Error' },
        { value: 'pending', label: 'Pending' },
      ] },
      { name: 'durationMs',      label: 'Duration (ms)',    type: 'number' },
      { name: 'requestPayload',  label: 'Request Payload',  type: 'textarea' },
      { name: 'responsePayload', label: 'Response Payload', type: 'textarea' },
      { name: 'errorMessage',    label: 'Error Message',    type: 'textarea' },
      { name: 'executedAt',      label: 'Executed At',      type: 'datetime' },
    ],
  },
};

// Compliance (WoF + License) + Dispatch line items
export const COMPLIANCE_CONFIGS: Record<string, CrudConfig> = {
  'wof-renewals': {
    title: 'WoF / CoF Renewals', singular: 'WoF/CoF', endpoint: '/wof-renewals', formSlug: 'wof_renewals', pageSlug: 'wof_renewals_page',
    columns: [
      { key: 'asset.fleetNumber', label: 'Vehicle',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || r.vehicle?.fleetNumber || '—' },
      { key: 'wofCof', label: 'Type', filterType: 'enum',
        enumOptions: [{ value: 'WoF', label: 'WoF' }, { value: 'CoF', label: 'CoF' }] },
      { key: 'testingDate', label: 'Tested', filterType: 'date',
        render: (r) => fmtDate(r.testingDate) },
      { key: 'expiryDate', label: 'Expires', filterType: 'date',
        render: (r) => fmtDate(r.expiryDate) },
      { key: 'processed', label: 'Processed', filterType: 'boolean',
        render: (r) => r.processed ? 'Yes' : 'No' },
    ],
    fields: [
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'wofCof', label: 'WoF / CoF', type: 'select', options: [{ value: 'WoF', label: 'WoF' }, { value: 'CoF', label: 'CoF' }] },
      { name: 'testingDate', label: 'Testing Date', type: 'date' },
      { name: 'expiryDate', label: 'Expiry Date', type: 'date' },
      { name: 'processed', label: 'Processed', type: 'boolean' },
    ],
  },
  'license-renewals': {
    title: 'License Renewals', singular: 'Renewal', endpoint: '/license-renewals', formSlug: 'license_renewals', pageSlug: 'license_renewals_page',
    columns: [
      { key: 'asset.fleetNumber', label: 'Vehicle',
        filterType: 'text', filterField: 'asset',
        render: (r) => r.asset?.fleetNumber || r.vehicle?.fleetNumber || '—' },
      { key: 'dateOfIssue', label: 'Issued', filterType: 'date',
        render: (r) => fmtDate(r.dateOfIssue) },
      { key: 'licencePeriod', label: 'Period (mo)', filterType: 'number' },
      { key: 'nztaReference', label: 'NZTA Ref', filterType: 'text' },
      { key: 'purchaseAmount', label: 'Cost', filterType: 'number' },
      { key: 'processed', label: 'Processed', filterType: 'boolean',
        render: (r) => r.processed ? 'Yes' : 'No' },
    ],
    fields: [
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
      { name: 'dateOfIssue', label: 'Date of Issue', type: 'date' },
      { name: 'licencePeriod', label: 'Licence Period (months)', type: 'number' },
      { name: 'nztaReference', label: 'NZTA Reference', type: 'text' },
      { name: 'purchaseAmount', label: 'Purchase Amount', type: 'number' },
      { name: 'processed', label: 'Processed', type: 'boolean' },
    ],
  },
  'dispatch-job-products': {
    title: 'Dispatch Job Products', singular: 'Line Item', endpoint: '/dispatch-job-products',
    detailTitle: (r) => joinTitle(refField(r.dispatchJob, 'jobNumber'), refField(r.catalogueItem, 'itemCode')),
    columns: [
      { key: 'dispatchJob.jobNumber', label: 'Job #',
        filterType: 'text', filterField: 'dispatchJob',
        render: (r) => r.dispatchJob?.jobNumber || '—' },
      { key: 'catalogueItem.itemCode', label: 'Item',
        filterType: 'text', filterField: 'catalogueItem',
        render: (r) => r.catalogueItem?.itemCode || '—' },
      { key: 'catalogueItem.description', label: 'Description',
        render: (r) => r.catalogueItem?.description || '—' },
      { key: 'quantity', label: 'Qty', filterType: 'number' },
      { key: 'unit', label: 'Unit', filterType: 'text' },
    ],
    fields: [
      { name: 'dispatchJob', label: 'Dispatch Job', type: 'ref', refEndpoint: '/dispatch/jobs', refLabel: 'jobNumber' },
      { name: 'catalogueItem', label: 'Catalogue Item', type: 'ref', refEndpoint: '/catalogue-items', refLabel: 'itemCode' },
      { name: 'quantity', label: 'Quantity', type: 'number' },
      { name: 'unit', label: 'Unit', type: 'text' },
    ],
  },
};

// Subform answers, lifecycle workflows, Zoho meta
export const AUDIT_CONFIGS: Record<string, CrudConfig> = {
  'service-history-checklist-answers': {
    title: 'Service Checklist Answers', singular: 'Answer', endpoint: '/service-history-checklist-answers',
    detailTitle: (r) => joinTitle(r.task) ?? joinTitle(refField(r.checkList, 'listItem')),
    columns: [
      { key: 'task', label: 'Task', filterType: 'text' },
      { key: 'answer', label: 'Answer', filterType: 'enum',
        enumOptions: [
          { value: 'OK', label: 'OK' },
          { value: 'Not OK', label: 'Not OK' },
          { value: 'N/A', label: 'N/A' },
        ] },
      { key: 'comments', label: 'Comments', filterType: 'text' },
      { key: 'checkList.listItem', label: 'Checklist item',
        filterType: 'text', filterField: 'checkList',
        render: (r) => r.checkList?.listItem || '—' },
    ],
    fields: [
      { name: 'task', label: 'Task', type: 'text' },
      { name: 'answer', label: 'Answer', type: 'select', options: [
        { value: 'OK', label: 'OK' },
        { value: 'Not OK', label: 'Not OK' },
        { value: 'N/A', label: 'N/A' },
      ] },
      { name: 'comments', label: 'Comments', type: 'textarea' },
      { name: 'serviceHistory', label: 'Service History', type: 'ref', refEndpoint: '/servicing/history', refLabel: 'jobSheetNumber' },
      { name: 'checkList', label: 'Checklist Item', type: 'ref', refEndpoint: '/checklists', refLabel: 'listItem' },
    ],
  },
  'asset-lifecycle-stages': {
    title: 'Asset Lifecycle Stages', singular: 'Stage', endpoint: '/asset-lifecycle-stages',
    detailTitle: (r) => joinTitle(r.stage) ?? joinTitle(r.pointDescription),
    canCreate: false, canDelete: false,
    columns: [
      { key: 'kind', label: 'Kind', filterType: 'enum',
        enumOptions: [{ value: 'onboard', label: 'Onboard' }, { value: 'transfer', label: 'Transfer' }] },
      { key: 'pointLink', label: 'Point Link', filterType: 'text' },
      { key: 'pointDescription', label: 'Description', filterType: 'text' },
      { key: 'sourceField', label: 'Source Field', filterType: 'text' },
      { key: 'assetField', label: 'Asset Field', filterType: 'text' },
    ],
    fields: [
      { name: 'kind', label: 'Kind', type: 'select', required: true, options: [{ value: 'onboard', label: 'Onboard' }, { value: 'transfer', label: 'Transfer' }] },
      { name: 'pointLink', label: 'Point Link', type: 'text' },
      { name: 'pointDescription', label: 'Description', type: 'text' },
      { name: 'stage', label: 'Stage', type: 'text' },
      { name: 'sourceField', label: 'Source Field', type: 'select',
        optionsEndpoint: (form) => {
          if (form.kind === 'onboard') return '/meta/model-fields/onboard-asset';
          if (form.kind === 'transfer') return '/meta/model-fields/asset-transfer';
          return null;
        },
        placeholder: 'Pick the workflow field that drives this stage' },
      { name: 'assetField', label: 'Asset Field to Update', type: 'select',
        optionsEndpoint: '/meta/model-fields/asset',
        placeholder: 'Pick the Asset field — leave None for compliance-only steps' },
    ],
  },
  'asset-settings': {
    title: 'Onboarding & Transfer Configurations', singular: 'Configuration', endpoint: '/asset-settings',
    detailTitle: (r) => joinTitle(r.pointName) ?? joinTitle(r.settingsName),
    columns: [
      { key: 'kind', label: 'Kind', filterType: 'enum',
        enumOptions: [{ value: 'onboard', label: 'Onboard' }, { value: 'transfer', label: 'Transfer' }] },
      { key: 'settingsName', label: 'Name', filterType: 'text' },
      { key: 'apply', label: 'Active', filterType: 'boolean',
        render: (r) => r.apply ? 'Yes' : '—' },
    ],
    rowActions: [
      { label: 'Activate',
        endpoint: '/asset-settings/:id/activate',
        actionKey: 'asset-setting.activate',
        confirm: 'Activate this configuration? The currently active one for this kind will become inactive.',
        visible: (r) => !r.apply },
    ],
    fields: [
      { name: 'kind', label: 'Kind', type: 'select', required: true, options: [{ value: 'onboard', label: 'Onboard' }, { value: 'transfer', label: 'Transfer' }] },
      { name: 'settingsName', label: 'Name', type: 'text' },
    ],
    relatedLists: [
      { title: 'Tasks', endpoint: '/asset-setting-tasks', filterField: 'setting',
        rowEditSingular: 'Task',
        columns: [
          { key: 'pointName', label: 'Point' },
          { key: 'fieldType', label: 'Input' },
          { key: 'assetField', label: 'Updates Asset Field' },
          { key: 'required', label: 'Required', render: (r) => r.required ? 'Yes' : '—' },
          { key: 'responsiblePerson.fullName', label: 'Responsible',
            render: (r) => r.responsiblePerson?.fullName || '—' },
          { key: 'otherStaff', label: 'Other Staff',
            render: (r) => Array.isArray(r.otherStaff) && r.otherStaff.length
              ? r.otherStaff.map((s: any) => s?.fullName).filter(Boolean).join(', ')
              : '—' },
        ],
        rowEditFields: [
          { name: 'pointName', label: 'Point Name', type: 'text', required: true },
          { name: 'pointDescription', label: 'Description', type: 'textarea' },
          { name: 'fieldType', label: 'Input Type', type: 'select', required: true, options: [
            { value: 'checkbox', label: 'Checkbox (yes/no)' },
            { value: 'text', label: 'Text' },
            { value: 'number', label: 'Number' },
            { value: 'date', label: 'Date' },
            { value: 'select', label: 'Select (dropdown)' },
          ] },
          { name: 'assetField', label: 'Updates Asset Field', type: 'select',
            optionsEndpoint: '/meta/model-fields/asset',
            placeholder: 'Asset field — leave None for compliance-only tasks' },
          { name: 'required', label: 'Required', type: 'boolean' },
          { name: 'responsiblePerson', label: 'Responsible Person', type: 'ref',
            refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
          { name: 'otherStaff', label: 'Other Staff', type: 'multi-ref',
            refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
        ],
      },
    ],
  },
  'asset-setting-tasks': {
    title: 'Configuration Tasks', singular: 'Task', endpoint: '/asset-setting-tasks',
    detailTitle: (r) => joinTitle(r.pointName) ?? joinTitle(refField(r.setting, 'settingsName')),
    columns: [
      { key: 'setting.settingsName', label: 'Configuration',
        filterType: 'text', filterField: 'setting',
        render: (r) => r.setting?.settingsName || '—' },
      { key: 'pointName', label: 'Point', filterType: 'text' },
      { key: 'sourceField', label: 'Source Field', filterType: 'text' },
      { key: 'assetField', label: 'Asset Field', filterType: 'text' },
      { key: 'responsiblePerson.fullName', label: 'Responsible',
        filterType: 'text', filterField: 'responsiblePerson',
        render: (r) => r.responsiblePerson?.fullName || '—' },
    ],
    fields: [
      { name: 'kind', label: 'Kind', type: 'select', required: true, options: [{ value: 'onboard', label: 'Onboard' }, { value: 'transfer', label: 'Transfer' }] },
      { name: 'setting', label: 'Configuration', type: 'ref', refEndpoint: '/asset-settings', refLabel: 'settingsName',
        dependsOn: { formField: 'kind', refField: 'kind' } },
      { name: 'pointName', label: 'Point Name', type: 'text' },
      { name: 'pointDescription', label: 'Description', type: 'textarea' },
      { name: 'pointLink', label: 'Point Link (stable ID)', type: 'text' },
      { name: 'sourceField', label: 'Source Field', type: 'select',
        optionsEndpoint: (form) => {
          if (form.kind === 'onboard') return '/meta/model-fields/onboard-asset';
          if (form.kind === 'transfer') return '/meta/model-fields/asset-transfer';
          return null;
        },
        placeholder: 'Field on the workflow record that drives this task' },
      { name: 'assetField', label: 'Asset Field to Update', type: 'select',
        optionsEndpoint: '/meta/model-fields/asset',
        placeholder: 'Asset field — leave None for compliance-only tasks' },
      { name: 'decisionBox', label: 'Decision Box', type: 'boolean' },
      { name: 'responsiblePerson', label: 'Responsible Person', type: 'ref', refEndpoint: '/employees', refLabel: 'fullName', refQuery: EMPLOYEE_REF_QUERY },
    ],
  },
  'asset-onboard-email-logs': {
    title: 'Onboard Email Log', singular: 'Email', endpoint: '/asset-onboard-email-logs',
    detailTitle: (r) => joinTitle(r.emailSubject),
    columns: [
      { key: 'emailSubject', label: 'Subject', filterType: 'text' },
      { key: 'recipients', label: 'Recipients', filterType: 'text' },
      { key: 'replyTo', label: 'Reply To', filterType: 'text' },
    ],
    fields: [
      { name: 'emailSubject', label: 'Subject', type: 'text' },
      { name: 'message', label: 'Message', type: 'textarea' },
      { name: 'recipients', label: 'Recipients', type: 'textarea' },
      { name: 'replyTo', label: 'Reply To', type: 'text' },
    ],
  },
  'asset-request-selecteds': {
    title: 'Asset Request Selections', singular: 'Selection', endpoint: '/asset-request-selecteds',
    columns: [
      { key: 'fleet', label: 'Fleet', filterType: 'text' },
      { key: 'markAs', label: 'Mark As', filterType: 'text' },
      { key: 'reason', label: 'Reason', filterType: 'text' },
    ],
    fields: [
      { name: 'fleet', label: 'Fleet', type: 'text' },
      { name: 'markAs', label: 'Mark As', type: 'text' },
      { name: 'reason', label: 'Reason', type: 'text' },
      { name: 'asset', label: 'Asset', type: 'ref', refEndpoint: '/fleet/assets', refLabel: 'fleetNumber', refQuery: ASSET_REF_QUERY },
    ],
  },
  'zoho-form-catalogs': {
    title: 'Zoho Form Catalog', singular: 'Form', endpoint: '/zoho-form-catalogs',
    columns: [
      { key: 'name', label: 'Name', filterType: 'text' },
      { key: 'linkName', label: 'Link Name', filterType: 'text' },
      { key: 'typeField', label: 'Type', filterType: 'text' },
    ],
    fields: [
      { name: 'name', label: 'Name', type: 'text' },
      { name: 'linkName', label: 'Link Name', type: 'text' },
      { name: 'typeField', label: 'Type', type: 'text' },
    ],
    relatedLists: [
      { title: 'Accessibility grants for this form', endpoint: '/accessibility-grants', filterField: 'form',
        columns: [
          { key: 'target', label: 'Target' },
          { key: 'role.name', label: 'Role', render: (r) => r.role?.name || '—' },
          { key: 'view', label: 'View', render: (r) => r.view ? '✓' : '' },
          { key: 'edit', label: 'Edit', render: (r) => r.edit ? '✓' : '' },
          { key: 'delete', label: 'Delete', render: (r) => r.delete ? '✓' : '' },
        ] },
    ],
  },
  'zoho-migration-records': {
    title: 'Zoho Migration Log', singular: 'Record', endpoint: '/zoho-migration-records',
    detailTitle: (r) => joinTitle(r.newAssets),
    columns: [
      { key: 'zohoAddedTime', label: 'Logged', filterType: 'date',
        render: (r) => fmtDateTime(r.zohoAddedTime) },
    ],
    fields: [
      { name: 'newAssets', label: 'New Assets', type: 'text' },
      { name: 'oldToNewAssetsUpdate', label: 'Old→New Update', type: 'text' },
    ],
  },
  'accessibility-grants': {
    title: 'Legacy Accessibility Grants', singular: 'Grant', endpoint: '/accessibility-grants',
    detailTitle: (r) => joinTitle(refField(r.role, 'name'), refField(r.form, 'name') ?? r.sectionId),
    columns: [
      { key: 'target', label: 'Target', filterType: 'enum',
        enumOptions: [
          { value: 'form', label: 'Form' }, { value: 'page', label: 'Page' }, { value: 'report', label: 'Report' },
        ] },
      { key: 'role.name', label: 'Role',
        filterType: 'text', filterField: 'role',
        render: (r) => r.role?.name || '—' },
      { key: 'form.name', label: 'Form',
        filterType: 'text', filterField: 'form',
        render: (r) => r.form?.name || '—' },
      { key: 'view', label: 'View', filterType: 'boolean',
        render: (r) => r.view ? '✓' : '' },
      { key: 'edit', label: 'Edit', filterType: 'boolean',
        render: (r) => r.edit ? '✓' : '' },
    ],
    fields: [
      { name: 'target', label: 'Target', type: 'select', required: true, options: [
        { value: 'form', label: 'Form' }, { value: 'page', label: 'Page' }, { value: 'report', label: 'Report' },
      ] },
      { name: 'role', label: 'Role', type: 'ref', refEndpoint: '/roles', refLabel: 'name' },
      { name: 'form', label: 'Form', type: 'ref', refEndpoint: '/zoho-form-catalogs', refLabel: 'name' },
      { name: 'sectionId', label: 'Section ID', type: 'text' },
      { name: 'add', label: 'Add', type: 'boolean' },
      { name: 'view', label: 'View', type: 'boolean' },
      { name: 'edit', label: 'Edit', type: 'boolean' },
      { name: 'editOwnRecord', label: 'Edit Own Record', type: 'boolean' },
      { name: 'delete', label: 'Delete', type: 'boolean' },
      { name: 'customButton', label: 'Custom Button', type: 'boolean' },
    ],
  },
  'section-headers': {
    title: 'UI Section Headers', singular: 'Header', endpoint: '/section-headers',
    detailTitle: (r) => joinTitle(r.sectionName) ?? joinTitle(r.sectionId),
    columns: [
      { key: 'sectionId', label: 'Section ID', filterType: 'text' },
      { key: 'sectionName', label: 'Name', filterType: 'text' },
    ],
    fields: [
      { name: 'sectionId', label: 'Section ID', type: 'text' },
      { name: 'sectionName', label: 'Name', type: 'text' },
    ],
  },
  'product-selections': {
    title: 'Product Selections', singular: 'Selection', endpoint: '/product-selections',
    columns: [
      { key: 'kind', label: 'Kind', filterType: 'enum',
        enumOptions: [{ value: 'header', label: 'Header' }, { value: 'quantity', label: 'Quantity' }] },
      { key: 'itemId', label: 'Item ID', filterType: 'text' },
      { key: 'description', label: 'Description', filterType: 'text' },
      { key: 'quantity', label: 'Qty', filterType: 'number' },
    ],
    fields: [
      { name: 'kind', label: 'Kind', type: 'select', required: true, options: [{ value: 'header', label: 'Header' }, { value: 'quantity', label: 'Quantity' }] },
      { name: 'itemId', label: 'Item ID', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'quantity', label: 'Quantity', type: 'number' },
    ],
  },
};
